import React, { useContext, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Form, Button, Segment, Loader } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import CustomIcon from '../CustomIcon';
import LoaderMilkCan from '../Loader';
import { El } from './style';
import { LazyLottie } from '../LazyLottie';
import { Nullable } from '~/types';
interface ILayoutProps {
  title?: string | React.ReactElement;
  isBusy?: boolean;
  children?: React.ReactNode;
  /** Either action URL or function to be called on action. */
  action?: string | (() => void);
  /** Either URL to go to on close, or function to be called on close. */
  close?: string | ((e: unknown) => void);
  actionName?: string;
  actions?: React.ReactElement | React.ReactElement[];
  onSubmit?: () => void;
  columns?: number;
  filters?: React.ReactElement,
  segmented?: boolean
  fontSizeHeader?: string,
  actionWidth?: string
  childContainerSize?: string
  useSpinner?: boolean
  disableSubmit?: boolean
  whiteSpaceHeaderProps?: CSSProperties['whiteSpace']
  childrenOverflow?: CSSProperties['overflow']
  isCo2Loader?: boolean
  removeHeader?: boolean
  animatedSpinner?: Nullable<{
    fileName: string
    text: string
  }>
  actionAlignToTop?: boolean
  actionNoWrap?: boolean
}

const Layout: React.FunctionComponent<ILayoutProps> = (props) => {
  const { children, action, actions, actionName, close, onSubmit, isBusy } = props;
  const { t } = useTranslation();
  const { firstColor } = useContext(ThemeContext)

  const renderCloseButton = () => {
    if (typeof close === 'string') {
      return (
        <El.PageHeaderActionBackButton as={Link} to={close}>
          <CustomIcon name='arrow alternate circle left' size='big' />
        </El.PageHeaderActionBackButton>
      );
    } else if (typeof close === 'function') {
      return (
        <El.PageHeaderActionBackButton as={'a'} type={'button'} onClick={close}>
          <CustomIcon name='arrow alternate circle left' size='big' />
        </El.PageHeaderActionBackButton>
      );
    }
  };

  const renderAction = () => {
    if (typeof action === 'function') {
      return (
        <El.PageHeaderActionButton
          positive size='large' onClick={action} disabled={isBusy}>
          {actionName || t('Create')}
        </El.PageHeaderActionButton>
      );
    }
    if (typeof action === 'string') {
      return (
        <Button
          size='large'
          style={{ backgroundColor: firstColor, color: 'white' }}
          as={onSubmit ? 'button' : Link}
          disabled={isBusy || Boolean(props.disableSubmit)}
          type={onSubmit ? 'submit' : 'button'}
          to={onSubmit ? null : action}
        >
          {t(onSubmit ? action : actionName || 'Create')}
        </Button>
      );
    }
  };

  const renderPageHeader = () => {
    return (
      <El.PageHeaderContainer $actionAlignToTop={props.actionAlignToTop}>
        <El.PageHeaderWrapper>
          {close && renderCloseButton()}
          {props.title && <El.PageHeaderTitle
            whiteSpaceHeaderProps={props.whiteSpaceHeaderProps}
            fontSizeHeader={props.fontSizeHeader}
            as='h1'
          >
            {props.title}
          </El.PageHeaderTitle>}
          {props.filters}
        </El.PageHeaderWrapper>
        <El.PageHeaderActions
          $actionNoWrap={props.actionNoWrap}
          actionWidth={props.actionWidth}
        >
          {actions}
          {action && renderAction()}
        </El.PageHeaderActions>
      </El.PageHeaderContainer>
    );
  };

  function renderFooter() {
    return <i>{'© FoodBusiness.Digital GmbH v1.9.02 16.03.2022'}</i>
  }

  function renderAnimatedSpinner() {
    return <El.DimmerContainer inverted active>
      <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <LazyLottie
          getAnimationData={() => import(`@assets/json-lottie/${props.animatedSpinner.fileName}.json`)}
          loop
          id={props.animatedSpinner.fileName}
          style={{ height: 250, width: 250 }}
        />
        <div style={{ color: 'black' }}>{props.animatedSpinner.text}</div>
      </div>
    </El.DimmerContainer>
  }

  return (
    <El.Layout
      $childContainerSize={props.childContainerSize}
      autoComplete={'off'}
      onSubmit={onSubmit}
      as={onSubmit ? Form : 'div'}
    >
      {!props.removeHeader && <El.HeaderWrapper as={props.segmented ? Segment : 'div'}>
        {renderPageHeader()}
      </El.HeaderWrapper>}
      <El.ChildContainer
        $childrenOverflow={props.childrenOverflow}
        $childContainerSize={props.childContainerSize}
        as={props.segmented ? Segment : 'div'}
        $titleRemoved={props.removeHeader}
      >
        {props.animatedSpinner && renderAnimatedSpinner()}
        <El.DimmerContainer inverted active={props.isBusy}>
          {props.useSpinner ? <Loader active={props.isBusy} /> : <LoaderMilkCan
            isLoading={props.isBusy}
            isCo2Loader={props.isCo2Loader}
          />}
        </El.DimmerContainer>
        {children}
      </El.ChildContainer>
      {/*   <Divider />
      {!props.childContainerSize && <El.FooterWrapper>
        {renderFooter()}
      </El.FooterWrapper>
      } */}
    </El.Layout>
  );
};

export default Layout;
